/**
 * Locale translations: de (German).
 * Keys and structure are shared across all locale files.
 */
export const de = {
  // Common
  common: {
    loading: "Laden...",
    error: "Fehler",
    sort: "Sortieren",
    sortAsc: "Aufsteigend sortieren",
    sortDesc: "Absteigend sortieren",
    save: "Speichern",
    cancel: "Abbrechen",
    delete: "Löschen",
    remove: "Entfernen",
    edit: "Bearbeiten",
    view: 'Ansehen',
    copy: "Kopieren",
    create: "Erstellen",
    update: "Aktualisieren",
    search: "Suchen",
    actions: "Aktionen",
    code: "Code",
    all: "Alle",
    clear: "Löschen",
    filter: "Filter",
    yes: "Ja",
    no: "Nein",
    confirm: "Bestätigen",
    close: "Schließen",
    select: "Auswählen",
    active: "Aktiv",
    inactive: "Inaktiv",
    success: "Erfolg",
    updated: "Aktualisiert",
    created: "Erstellt",
    deleted: "Gelöscht",
    confirmDelete: "Sind Sie sicher, dass Sie löschen möchten?",
    errorOccurred: "Ein Fehler ist aufgetreten",
    noData: "Keine Daten",
    status: "Status",
    show: "Anzeigen",
    hide: "Ausblenden",
    searchPlaceholder: "Suchen...",
    allStatuses: "Alle Status",
    clearFilters: "Filter löschen",
    newRecord: "Neuer Datensatz",
    noPermission: "Keine Berechtigung",
    statusDistribution: "Statusverteilung",
    rows: "Zeilen",
    firstPage: "Erste Seite",
    previousPage: "Vorherige Seite",
    nextPage: "Nächste Seite",
    lastPage: "Letzte Seite",
    page: "Seite",
    optional: "Optional",
    saving: "Wird gespeichert...",
    deleteConfirmTitle: "Löschen bestätigen",
    deleteWarning: "Diese Aktion kann nicht rückgängig gemacht werden!",
    autoTranslate: "Automatisch übersetzen",
    autoTranslateSuccessCount: "{count} Elemente automatisch übersetzt.",
    autoTranslateNoSource: "Keine Quellübersetzung gefunden.",
    enterTargetLanguage: "Bitte wählen Sie die Zielsprache aus.",
    sourceLanguageLabel: "Quellsprache (Ihre Oberfläche)",
    targetLanguage: "Zielsprache",
    sourceAndTargetMustDiffer: "Quell- und Zielsprache müssen sich unterscheiden.",
    context: "Kontext",
    key: "Schlüssel",
    value: "Wert",
    locale: "Sprache",
    auto: "Auto",
    manual: "Manuell",
    manageLocales: "Sprachen verwalten",
    activeLocalesDescription: "Wählen Sie aktive Sprachen aus. Wird nur als Einstellung gespeichert.",
    updateUntranslatedFields: "Neue Felder synchronisieren",
    syncEntriesResult: "{count} neue Übersetzungseinträge synchronisiert.",
    syncDeletedResult: "{count} veraltete Einträge gelöscht.",
    selectSourceLanguageForSync: "In welcher Sprache ist der Inhalt?",
    sourceLanguage: "Quellsprache",
    originalShort: "Orig.",
    languageNames: { en: "Englisch", tr: "Türkisch", de: "Deutsch", fr: "Französisch", es: "Spanisch", ar: "Arabisch", it: "Italienisch", ja: "Japanisch", zh: "Chinesisch", ru: "Russisch" } as Record<string, string>,
    perPage: "Pro Seite",
    darkMode: "Dunkelmodus",
    lightMode: "Hellmodus",
    copyright: "© {year} QubePro. Alle Rechte vorbehalten.",
    translationRates: "Übersetzungsraten",
    profileMenu: "Profilmenü",
    manageYourAccount: "Konto verwalten",
    adminConsole: "Admin-Konsole",
    serverConfiguring: "Server-Konfiguration, bitte warten...",
    productName: "Management",
  },

  portal: {
    searchPlaceholder: "Apps suchen...",
    noAppsFound: "Keine Apps gefunden.",
    quickLinks: "Quicklinks",
    console: "Admin-Konsole",
    lastLogin: "Letzter Login",
    apps: {
      core: { name: "Management", description: "Verwalten Sie Unternehmen, Organisationen, Benutzer, Rollen und Berechtigungen auf einer zentralen Plattform." },
      bi: { name: "Analytics", description: "Erstellen Sie Berichte und Dashboards aus Ihren Daten. Verwandeln Sie Geschäftsdaten in Erkenntnisse." },
      bpm: { name: "Prozesse", description: "Starten, verfolgen und erledigen Sie interne Anfragen, Genehmigungen und Aufgaben digital." },
      support: { name: "Support", description: "Verwalten Sie Kundenanfragen und Tickets. Beheben Sie Probleme an einem Ort." },
      agent: { name: "Automatisierung", description: "Automatisieren Sie Routineaufgaben mit Bots und Agenten. Integration Ihrer Tools." },
      ai: { name: "KI & Prognosen", description: "Nutzen Sie KI-Modelle für Datenanalyse und Vorhersagen. Verbessern Sie Planung und Entscheidungen." },
      erp: { name: "Business Suite", description: "Module für Finanzen, Vertrieb, Lager und Betrieb. Führen Sie Ihr Unternehmen auf einer Plattform." },
      pms: { name: "Projekte", description: "Planen Sie Projekte, weisen Sie Aufgaben zu, verfolgen Sie den Fortschritt und arbeiten Sie im Team." },
      crm: { name: "Kundenmanagement", description: "Verwalten Sie Kontakte, Leads, Deals und Sales-Pipelines. Zentralisieren Sie Kundeninfos." },
    },
    menus: {
      core_dashboard: "Dashboard",
      core_companies: "Unternehmen",
      core_organizations: "Organisationen",
      core_roles: "Rollen",
      core_users: "Benutzer",
      core_groups: "Gruppen",
      core_emails: "E-Mail-Konten",
      core_data_sources: "Datenquellen",
      core_settings_company: "Unternehmenseinstellungen",
      core_settings_user: "Benutzereinstellungen",
      core_translations: "Übersetzungen",
    },
  },

  panel: {
    apps: {
      crm: { name: "CRM", footerName: "CRM" },
      accounting: { name: "Buchhaltung" },
      finance: { name: "Finanzen" },
      procurement: { name: "Beschaffung" },
      inventory: { name: "Lager" },
      purchase: { name: "Einkauf" },
      sales: { name: "Verkauf" },
      manufacturing: { name: "Fertigung" },
      assets: { name: "Anlagen" },
      hr: { name: "Personal" },
      project: { name: "Projektmanagement" },
      timesheet: { name: "Zeiterfassung" },
      documents: { name: "Dokumente" },
      pos: { name: "Point of Sale" },
      rental: { name: "Vermietung" },
      dashboard: { name: "Berichte" },
    },
    appsLabel: "Anwendungen",
    settingsLabel: "Einstellungen",
    coreLabel: "Management",
    backToHome: "Zurück zum Start",
  },

  errors: {
    unknown: "Ein unbekannter Fehler ist aufgetreten",
    network: "Verbindung zum Server fehlgeschlagen. Prüfen Sie Ihr Internet.",
    timeout: "Zeitüberschreitung. Bitte versuchen Sie es erneut.",
    badRequest: "Ungültige Anfrage",
    unauthorized: "Sitzung abgelaufen. Bitte melden Sie sich erneut an.",
    forbidden: "Sie haben keine Berechtigung für diese Aktion.",
    notFound: "Ressource nicht gefunden",
    tooManyRequests: "Zu viele Anfragen. Bitte später erneut versuchen.",
    internalServerError: "Serverfehler. Bitte später erneut versuchen.",
    badGateway: "Server vorübergehend nicht erreichbar",
    serviceUnavailable: "Dienst vorübergehend nicht verfügbar",
    gatewayTimeout: "Server antwortet nicht",
    serverErrorWithStatus: "Serverfehler ({status})",
  },

  // Dashboard
  dashboard: {
    title: "Dashboard",
    subtitle: "Zusammenfassung des zentralen Managementsystems",
    welcome: "Willkommen",
    guestName: "Gast",
    statusSummary: "Aktueller Status-Überblick",
    currentTimeLabel: "Aktuelle Uhrzeit",
    todayLabel: "Heute",
    activeCompanies: "Aktive Unternehmen",
    activeOrganizations: "Aktive Organisationen",
    activeUsers: "Aktive Benutzer",
    activeRoles: "Aktive Rollen",
    database: "Datenbank",
    databaseUsed: "Genutzter Speicher",
    recentlyAddedUsers: "Zuletzt hinzugefügte Benutzer",
    recentlyAddedCompanies: "Zuletzt hinzugefügte Unternehmen",
    noUsersYet: "Noch keine Benutzer vorhanden",
    noCompaniesYet: "Noch keine Unternehmen vorhanden",
    userPanel: "Benutzer-Panel",
    userPanelWelcome: "Willkommen! Benutzereinstellungen sind über das Menü erreichbar.",
    quickActions: "Schnellaktionen",
    systemHealthTitle: "Systemzustand",
    systemLogsTitle: "Systemprotokolle",
    logChartTitle: "Fehler/Warnungsverteilung",
    logErrorsLabel: "Fehler",
    logWarningsLabel: "Warnungen",
    logSuccessLabel: "Erfolgreich",
    statusHealthy: "Gesund",
    statusFailed: "Fehlgeschlagen",
    statusUnknown: "Unbekannt",
    lastRun: "Letzter Lauf",
    failures24h: "Fehler (24h)",
    noData: "Keine Daten",
    systemComponents: {
      EMAIL_INGEST: "Eingang (IMAP)",
      EMAIL_DISPATCH: "Ausgang (SMTP)",
      JOB_SCHEDULER: "Scheduler",
      API: "API",
    },
  },

  // Sidebar
  sidebar: {
    dashboard: "Dashboard",
    roles: "Rollen",
    users: "Benutzer",
    settings: "Einstellungen",
    systemSettings: "Integration & Einstellungen",
    userSettings: "Benutzereinstellungen",
    organizations: "Organisationen",
    logout: "Abmelden",
    toggleMenu: "Menü umschalten",
    toggleMenuClose: "Menü schließen",
    management: "Management",
    companies: "Unternehmen",
    emailAccounts: "E-Mail-Konten",
    dataSources: "Datenquellen",
    translations: "Übersetzungen",
  },

  // Settings
  settings: {
    title: "Einstellungen",
    subtitle: "Unternehmens- und Benutzereinstellungen verwalten",
    companySettingsSubtitle: "Allgemeine Unternehmenseinstellungen",
    userSettingsSubtitle: "Persönliche Einstellungen",
    securitySettingsSubtitle: "Passwort und Sicherheit verwalten",
    companySettings: "Unternehmenseinstellungen",
    userSettings: "Benutzereinstellungen",
    general: "Allgemein",
    personal: "Persönlich",
    timezone: "Zeitzone",
    dateFormat: "Datumsformat",
    defaultCurrency: "Standardwährung",
    defaultLanguage: "Standardsprache",
    language: "Sprache",
    companySettingsSaved: "Unternehmenseinstellungen gespeichert",
    userSettingsSaved: "Benutzereinstellungen gespeichert",
    failedToLoad: "Einstellungen konnten nicht geladen werden",
    saveFailed: "Speichern der Einstellungen fehlgeschlagen",
    profilePicture: "Profilbild",
    maxFileSize: "Max. 5MB. Formate: JPEG, PNG, GIF, WebP",
    change: "Ändern",
    uploading: "Wird hochgeladen...",
    deleteAvatar: "Bild löschen",
    deleteAvatarConfirm: "Profilbild wirklich löschen?",
    avatarUploaded: "Profilbild erfolgreich hochgeladen",
    avatarDeleted: "Profilbild gelöscht",
    avatarUploadError: "Fehler beim Hochladen",
    avatarDeleteError: "Fehler beim Löschen",
    onlyImageFiles: "Nur Bilddateien erlaubt",
    fileSizeError: "Datei muss kleiner als 5MB sein",
    appearance: "Aussehen",
    theme: "Thema",
    light: "Hell",
    dark: "Dunkel",
    pageSize: "Tabellen-Seitengröße",
    defaultCompany: "Standardunternehmen",
    selectDefaultCompany: "Standardunternehmen auswählen",
    noCompaniesAssigned: "Keine zugewiesenen Unternehmen",
    security: "Sicherheit",
    changePassword: "Passwort ändern",
    currentPassword: "Aktuelles Passwort",
    newPassword: "Neues Passwort",
    confirmNewPassword: "Neues Passwort bestätigen",
    passwordChanged: "Passwort erfolgreich geändert",
    passwordChangeFailed: "Passwortänderung fehlgeschlagen",
    passwordMismatch: "Neue Passwörter stimmen nicht überein",
    passwordTooShort: "Passwort muss mindestens 6 Zeichen lang sein",
    passwordSameAsOld: "Neues Passwort muss sich vom alten unterscheiden",
    changingPassword: "Passwort wird geändert...",
    pleaseSelectCompany: "Bitte Unternehmen auswählen",
  },

  // Organizations
  organizations: {
    title: "Organisationen",
    code: "Code",
    name: "Organisationsname",
    description: "Beschreibung",
    logo: "Logo",
    logoEmpty: "Kein Logo",
    uploadLogo: "Logo hochladen",
    status: "Status",
    smtp: "SMTP",
    imap: "IMAP",
    smtpEmailAccount: "SMTP-E-Mail-Konto",
    imapEmailAccount: "IMAP-E-Mail-Konto",
    active: "Aktiv",
    inactive: "Inaktiv",
    newOrganization: "Neue Organisation",
    editOrganization: "Organisation bearbeiten",
    deleteConfirm: "Diese Organisation löschen?",
    deleteFailed: "Löschen der Organisation fehlgeschlagen",
    operationFailed: "Vorgang fehlgeschlagen",
    failedToLoad: "Organisationen konnten nicht geladen werden",
    dataFormatError: "Unerwartetes Format der Organisationsdaten",
    searchPlaceholder: "Organisation suchen (Name, Beschreibung)",
    empty: "Keine Daten",
    notSet: "Nicht festgelegt",
    noCreatePermission: "Keine Berechtigung zum Erstellen",
    noUpdatePermission: "Keine Berechtigung zum Bearbeiten",
    noDeletePermission: "Keine Berechtigung zum Löschen",
    company: "Unternehmensname",
    statusDistribution: "Statusverteilung der Organisationen",
  },

  // Users
  users: {
    title: "Benutzer",
    newUser: "Neuer Benutzer",
    editUser: "Benutzer bearbeiten",
    noUsersYet: "Noch keine Benutzer",
    failedToLoad: "Benutzer konnten nicht geladen werden",
    deleteConfirm: "Diesen Benutzer löschen?",
    deleteFailed: "Löschen fehlgeschlagen",
    operationFailed: "Vorgang fehlgeschlagen",
    passwordRequired: "Passwort ist erforderlich",
    passwordChangeHint: "(Ausfüllen zum Ändern)",
    fullName: "Vollständiger Name",
    email: "E-Mail",
    status: "Status",
    roles: "Rollen",
    actions: "Aktionen",
    firstName: "Vorname *",
    lastName: "Nachname *",
    password: "Passwort",
    noRoles: "Keine Rollen",
    rolesNotFound: "Rollen nicht gefunden",
    statusActive: "Aktiv",
    statusInactive: "Inaktiv",
    statusSuspended: "Gesperrt",
    userInfoFailed: "Fehler beim Laden der Benutzerinfos",
    rolesFailed: "Fehler beim Laden der Rollen",
    copy: "Kopieren",
    isAdmin: "Admin",
    groups: "Gruppen",
    groupCount: "Anzahl Gruppen",
    userGroupName: "Gruppenname",
    userGroupCode: "Gruppencode",
    userGroupEmail: "Gruppen-E-Mail",
    groupManager: "Gruppenleiter",
    members: "Mitglieder",
    userGroupDescription: "Beschreibung",
    userGroupCreate: "Neue Gruppe erstellen",
    userGroupEdit: "Gruppe bearbeiten",
    manageMembers: "Mitglieder verwalten",
    addMember: "Mitglied hinzufügen",
    memberCount: "Anzahl Mitglieder",
    searchUsers: "Benutzer suchen...",
    noMembers: "Noch keine Mitglieder",
    selectUser: "Benutzer auswählen",
    addUser: "Hinzufügen",
    removeUser: "Entfernen",
    manageGroups: "Gruppen verwalten",
    noUsersFound: "Keine Benutzer gefunden",
    statusDistribution: "Statusverteilung der Benutzer",
    groupStatusDistribution: "Statusverteilung der Gruppen",
  },

  // Roles
  roles: {
    title: "Rollen",
    newRole: "Neue Rolle",
    editRole: "Rolle bearbeiten",
    noRolesYet: "Noch keine Rollen",
    failedToLoad: "Rollen konnten nicht geladen werden",
    deleteConfirm: "Diese Rolle löschen?",
    deleteFailed: "Löschen fehlgeschlagen",
    operationFailed: "Vorgang fehlgeschlagen",
    dataFormatError: "Datenformatfehler",
    permissionsFailed: "Fehler beim Laden der Berechtigungen",
    name: "Rollenname *",
    description: "Beschreibung",
    permissions: "Berechtigungen",
    permissionCount: "Anzahl Berechtigungen",
    type: "Rollentyp",
    typeSystem: "System",
    typeCustom: "Benutzerdefiniert",
    typeAll: "Alle Rollen",
    searchPlaceholder: "Suchen (Name, Beschreibung, Berechtigung)...",
    systemBadge: "System",
    permissionsCount: "Berechtigungen ({count})",
    morePermissions: "+ {count} weitere",
    copy: "Kopieren",
    copySuffix: " (Kopie)",
    clearFilters: "Filter löschen",
    noCreatePermission: "Keine Berechtigung zum Erstellen",
    noUpdatePermission: "Keine Berechtigung zum Bearbeiten",
    noDeletePermission: "Keine Berechtigung zum Löschen",
    systemRoleEditBlocked: "Systemrollen können nicht bearbeitet werden",
    systemRoleDeleteBlocked: "Systemrollen können nicht gelöscht werden",
    noPermissions: "Keine Berechtigungen",
    paginationRange: "{start}-{end} von {total}",
    paginationPage: "Seite {page} / {total}",
    modules: {
      users: "Benutzer",
      groups: "Gruppen",
      userGroupName: "Gruppenname",
      userGroupCode: "Gruppencode",
      groupManager: "Gruppenleiter",
      members: "Mitglieder",
      userGroupDescription: "Beschreibung",
      userGroupCreate: "Neue Gruppe erstellen",
      userGroupEdit: "Gruppe bearbeiten",
      roles: "Rollen",
      tenant: "Mandant",
      userSettingField: "Einstellung",
      settings: "Einstellungen",
      organizations: "Organisationen",
    },
    groups: "Gruppen",
    userGroup: "Benutzergruppe",
    userGroupList: "Gruppenliste",
    userGroupCreate: "Gruppe erstellen",
    userGroupEdit: "Gruppe bearbeiten",
    userGroupName: "Gruppenname",
    userGroupDescription: "Beschreibung",
    userGroupCode: "Code",
    groupManager: "Gruppenleiter",
    members: "Mitglieder",
    addMember: "Mitglied hinzufügen",
    assignRole: "Rolle zuweisen",
    assign: "Zuweisen",
    statusDistribution: "Statusverteilung der Rollen",
    assignRoleMatrix: "Rollen-Matrix",
    roleMatrix: "Rollen-Matrix",
    selectCompanyToViewMatrix: "Unternehmen auswählen, um Matrix anzuzeigen",
    show: "Anzeigen",
    hide: "Ausblenden",
    searchPermissions: "Berechtigungen suchen...",
    permission: "Berechtigung",
    permissionsSelected: "{count} Berechtigungen ausgewählt",
  },

  // Companies
  companies: {
    title: "Unternehmen",
    newCompany: "Neues Unternehmen",
    editCompany: "Unternehmen bearbeiten",
    noCompaniesYet: "Noch keine Unternehmen",
    failedToLoad: "Unternehmen konnten nicht geladen werden",
    deleteConfirm: "Dieses Unternehmen löschen?",
    deleteFailed: "Löschen fehlgeschlagen",
    operationFailed: "Vorgang fehlgeschlagen",
    dataFormatError: "Datenformatfehler",
    name: "Unternehmensname *",
    status: "Status",
    statusActive: "Aktiv",
    statusInactive: "Inaktiv",
    searchPlaceholder: "Suchen (Name, Status)...",
    clearFilters: "Filter löschen",
    copy: "Kopieren",
    copySuffix: " (Kopie)",
    noCreatePermission: "Keine Berechtigung zum Erstellen",
    noUpdatePermission: "Keine Berechtigung zum Bearbeiten",
    noDeletePermission: "Keine Berechtigung zum Löschen",
    paginationRange: "{start}-{end} von {total}",
    paginationPage: "Seite {page} / {total}",
    allStatuses: "Alle Status",
    statusDistribution: "Statusverteilung der Unternehmen",
    show: "Anzeigen",
    hide: "Ausblenden",
    logo: "Logo",
    logoEmpty: "Kein Logo",
    uploadLogo: "Logo hochladen",
    smtpEmailAccount: "SMTP-Konto",
    imapEmailAccount: "IMAP-Konto",
    smtpEmailId: "SMTP-Konto",
    imapEmailId: "IMAP-Konto",
  },

  // Emails
  emails: {
    title: "E-Mail-Konten",
    newEmail: "Neues Konto",
    editEmail: "Konto bearbeiten",
    noEmailsYet: "Noch keine Konten vorhanden",
    failedToLoad: "E-Mail-Konten konnten nicht geladen werden",
    deleteConfirm: "Dieses Konto löschen?",
    deleteConfirmMessage: "Möchten Sie das Konto \"{name}\" löschen?",
    deleteFailed: "Löschen fehlgeschlagen",
    operationFailed: "Vorgang fehlgeschlagen",
    dataFormatError: "Datenformatfehler",
    name: "Kontoname",
    type: "Typ",
    host: "Host",
    port: "Port",
    user: "Benutzername",
    password: "Passwort",
    fromEmail: "Absender-E-Mail",
    secure: "Sicher (SSL/TLS)",
    secureConnection: "Sichere Verbindung (SSL/TLS)",
    status: "Status",
    statusActive: "Aktiv",
    statusInactive: "Inaktiv",
    activate: "Aktivieren",
    deactivate: "Deaktivieren",
    isActive: "Aktiv",
    searchPlaceholder: "Suchen (Name, Host, Benutzer)...",
    clearFilters: "Filter löschen",
    copy: "Kopieren",
    noCreatePermission: "Keine Berechtigung zum Erstellen",
    noUpdatePermission: "Keine Berechtigung zum Bearbeiten",
    noDeletePermission: "Keine Berechtigung zum Löschen",
    paginationRange: "{start}-{end} von {total}",
    paginationPage: "Seite {page} / {total}",
    allStatuses: "Alle Status",
    allTypes: "Alle Typen",
    statusDistribution: "Statusverteilung der Konten",
    show: "Anzeigen",
    hide: "Ausblenden",
    nameRequired: "Kontoname ist obligatorisch",
    hostRequired: "Host ist obligatorisch",
    portRequired: "Port ist obligatorisch",
    userRequired: "Benutzername ist obligatorisch",
    passwordRequired: "Passwort ist obligatorisch",
    leaveBlankToKeep: "Leer lassen, um Aktuelles zu behalten",
    noData: "Keine Daten",
    noEmailsMessage: "Keine Konten gefunden",
    passwordEditHint: "Leer lassen, um nicht zu ändern",
    noSearchResults: "Keine Ergebnisse gefunden",
    namePlaceholder: "z.B. Support oder info@firma.de",
    deleteConfirmTitle: "Konto löschen",
  },

  // Login
  login: {
    title: "QubePro ERP",
    subtitle: "Enterprise Resource Planning",
    heroTitle: "Unternehmensverwaltung an einem Ort",
    heroSubtitle: "QubePro ERP bündelt die Verwaltung von Unternehmen, Organisationen, Benutzern und Rollen auf einer sicheren Plattform.",
    languageToggleLabel: "Sprache",
    turkish: "Türkisch",
    english: "Englisch",
    email: "E-Mail",
    password: "Passwort",
    emailPlaceholder: "beispiel@domain.de",
    emailHint: "Mandant wird automatisch anhand der Domain erkannt",
    passwordPlaceholder: "••••••••",
    loginButton: "Anmelden",
    loggingIn: "Anmeldung...",
    loginFailed: "Anmeldung fehlgeschlagen",
    darkMode: "Dunkelmodus",
    lightMode: "Hellmodus",
    loginPageTitle: "Anmeldung",
    submitButton: "Anmelden",
    featureTitle: "Zentrales Managementsystem",
    featureDescription: "Verwalten Sie Kundenbeziehungen, Vertrieb, Lager und Finanzprozesse in einer integrierten Plattform.",
    featureCard1Title: "Kundenverwaltung",
    featureCard1Description: "Verfolgen Sie Kunden, potenzielle Verkäufe und Geschäftsbeziehungen zentral.",
    featureCard2Title: "Vertrieb & Aufträge",
    featureCard2Description: "Verwalten Sie Angebote, Bestellungen und Vertriebsprozesse einfach und steigern Sie Ihren Umsatz.",
    featureCard3Title: "Berichte & Analysen",
    featureCard3Description: "Überwachen Sie die Unternehmensleistung mit Echtzeit-Berichten und treffen Sie datengestützte Entscheidungen.",
    featureCard4Title: "Systemeinstellungen",
    featureCard4Description: "Passen Sie Ihr System an, integrieren Sie andere Tools und automatisieren Sie Ihre Geschäftsprozesse.",
    pleaseWait: "Bitte warten",
    serverConfiguring: "Server-Konfiguration, bitte warten...",
    dbCreating: "Datenbank und notwendige Konfigurationen werden erstellt. Dies kann einige Sekunden dauern.",
    emailLabel: "E-Mail",
    passwordLabel: "Passwort",
    emailPlaceholderFull: "E-Mail-Adresse eingeben",
    passwordPlaceholderFull: "Passwort eingeben",
    showPassword: "Anzeigen",
    hidePassword: "Verbergen",
    emailRequired: "E-Mail ist erforderlich",
    passwordRequired: "Passwort ist erforderlich",
    backendUnavailable: "Server nicht erreichbar. Später erneut versuchen.",
    backendConnectFailed: "Verbindung fehlgeschlagen. Später erneut versuchen.",
    backendConnectFailedDev: "Verbindung fehlgeschlagen. Sicherstellen, dass Backend läuft ({apiUrl}).",
    fieldEmail: "E-Mail",
    fieldPassword: "Passwort",
    errors: {
      invalidCredentials: "E-Mail oder Passwort ungültig.",
      databaseUnavailable: "Datenbank nicht verfügbar oder Konfiguration nötig. Erneut anmelden.",
      accountInactive: "Konto inaktiv. Bitte Administrator kontaktieren.",
      sessionExpired: "Sitzung abgelaufen. Bitte erneut anmelden.",
      tooManyAttempts: "Zu viele Versuche. Später erneut versuchen.",
      sessionTooLarge: "Sitzungsdaten zu groß. Bitte erneut anmelden.",
    },
  },

  // DataSources
  dataSources: {
    title: "Datenquellen",
    subtitle: "Verbindungen zu Kundendatenbanken verwalten",
    newDataSource: "Neue Datenquelle",
    editDataSource: "Datenquelle bearbeiten",
    noDataSourcesYet: "Noch keine Datenquellen vorhanden",
    failedToLoad: "Datenquellen konnten nicht geladen werden",
    deleteConfirm: "Diese Datenquelle löschen?",
    deleteFailed: "Löschen fehlgeschlagen",
    operationFailed: "Vorgang fehlgeschlagen",
    dataFormatError: "Datenformatfehler",
    name: "Anzeigename",
    driver: "Treiber",
    server: "Server",
    port: "Port",
    databaseName: "Datenbankname",
    username: "Benutzername",
    password: "Passwort",
    schema: "Schema",
    sourceType: "Quelltyp",
    source: "Quelle",
    target: "Ziel",
    sourceSystem: "Quellsystem",
    targetSystem: "Zielsystem",
    status: "Status",
    statusActive: "Aktiv",
    statusInactive: "Inaktiv",
    searchPlaceholder: "Suchen (Name, Server, DB)...",
    clearFilters: "Filter löschen",
    copy: "Kopieren",
    paginationRange: "{start}-{end} / {total}",
    paginationPage: "Seite {page} / {total}",
    allStatuses: "Alle Status",
    allTypes: "Alle",
    statusDistribution: "Statusverteilung der Datenquellen",
    show: "Anzeigen",
    hide: "Ausblenden",
    nameRequired: "Name ist erforderlich",
    serverRequired: "Serveradresse ist erforderlich",
    databaseRequired: "Datenbankname ist erforderlich",
    passwordRequired: "Passwort ist erforderlich",
    testConnection: "Verbindung testen",
    testing: "Wird getestet...",
    testSuccess: "Verbindung erfolgreich!",
    testFailed: "Verbindung fehlgeschlagen: ",
    integratedSecurity: "Integrierte Sicherheit",
    ssl: "SSL",
    connectionTimeout: "Timeout (Sek.)",
    description: "Beschreibung",
    noCreatePermission: "Keine Berechtigung zum Erstellen",
    noUpdatePermission: "Keine Berechtigung zum Bearbeiten",
    noDeletePermission: "Keine Berechtigung zum Löschen",
  },

  aiProviders: {
    title: "KI-Anbieter",
    subtitle: "Management von KI-Modellen und Integrationen",
    newProvider: "Neuer KI-Anbieter",
    editProvider: "KI-Anbieter bearbeiten",
    noProvidersYet: "Keine Daten",
    failedToLoad: "KI-Anbieter konnten nicht geladen werden",
    deleteConfirm: "Sind Sie sicher, dass Sie diesen KI-Anbieter löschen möchten?",
    deleteFailed: "Löschen des KI-Anbieters fehlgeschlagen",
    operationFailed: "Vorgang fehlgeschlagen",
    name: "Anbietername *",
    provider: "Anbietertyp",
    baseUrl: "Basis-URL",
    apiKey: "API-Schlüssel",
    description: "Beschreibung",
    status: "Status",
    statusActive: "Aktiv",
    statusInactive: "Inaktiv",
    searchPlaceholder: "Suchen (Name, Typ, URL)...",
    clearFilters: "Filter löschen",
    noCreatePermission: "Keine Berechtigung zum Erstellen",
    statusDistribution: "Statusverteilung der KI-Anbieter",
    baseUrlPlaceholder: "z.B. http://localhost:11434, https://api.custom.ai/v1",
    baseUrlEmptyDefault: "Leer lassen für Standardwerte",
    apiKeyPlaceholder: "API-Schlüssel eingeben",
  },
};
